import { useQuery } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { ManifestReviewRow, STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY } from "./api";

/**
 * One-shot deep-link param for the manifest review queue: a URL carrying
 * `?manifest=<processedManifestId>` auto-opens that manifest's detail dialog,
 * then drops the param so closing the dialog doesn't reopen it.
 */
export const MANIFEST_REVIEW_MANIFEST_PARAM = "manifest";

/**
 * Consume the deep-link param by fetching the row by id — the query's
 * `processedManifestId` escape hatch returns the manifest whatever its
 * completion state, so the dialog opens no matter which tab is showing, what
 * the grid is filtered to, or which page the row would land on (the caller's
 * `manifestNumber` prefilter is a substring match over a non-unique MTN, so
 * page-1 presence was never guaranteed).
 */
export function useManifestReviewDeepLink(onOpen: (row: ManifestReviewRow) => void): void {
  const { error } = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkManifestId = searchParams.get(MANIFEST_REVIEW_MANIFEST_PARAM);

  const clearParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete(MANIFEST_REVIEW_MANIFEST_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useQuery(STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY, {
    skip: !deepLinkManifestId,
    variables: { processedManifestId: deepLinkManifestId },
    onCompleted: (data) => {
      const row = data.staffManifestReviewManifests.items[0];
      if (row) {
        onOpen(row);
      } else {
        // Stale link: manifests are deleted and re-minted under new ids by
        // reprocessing, so say what happened instead of silently doing nothing.
        error("That manifest is no longer in the review queue — it may have been reprocessed.");
      }
      clearParam();
    },
    onError: clearParam,
  });
}
